import { ref, readonly, computed } from "vue";
import { TRADITIONAL_FEED_ENABLED } from "@/constants/feedViewMode";
import { resolveBrandLogoSvg } from "@/utils/brandLogoSvg";
import { useSdk } from "./useSdk";
import type {
  AppSettings,
  AppReaction,
  AppBadge,
  AppGenre,
  SiteSettingsUI,
  NotificationType,
} from "@cc-consulting-nv/ccsdk";

// Reactive state (singleton)
const isLoaded = ref(false);
const isLoading = ref(false);
const error = ref<string | null>(null);
const lastLoadedAt = ref<number>(0);

// Settings data
// Default to empty - will be set from API response
const siteType = ref<string>("");
const siteLogo = ref<string | null>(null);
// Inline SVG markup for the brand mark, served by /v1/settings so tenants can
// brand the header/reel watermark without a UI deploy. When null, consumers
// should use brandLogoSvg (gunclub mark on gunclub hosts; generic communities
// mark elsewhere). (Backend: siteSettings.siteLogoSvg / logos.logoSvg.)
const siteLogoSvg = ref<string | null>(null);
const siteFavicon = ref<string | null>(null);
const siteWebsite = ref<string | null>(null);
// Label for the "Loot" sidebar/nav item, per-tenant via /v1/settings
// (ui-loot-menu-label tenant setting → ui.lootMenuLabel). Falls back to "Loot"
// at the consumer when null. e.g. nevadagolf → "GolfLoot", gunclub → "GunLoot".
const lootLabel = ref<string | null>(null);
// Headline shown under the Loot header (ui-loot-headline → ui.lootHeadline).
// No fallback — header hides the headline line when null.
const lootTagline = ref<string | null>(null);
const uiMode = ref<"light" | "dark" | "both">("both");
const uiSettings = ref<SiteSettingsUI>({});
// Legacy alias for backward compatibility
const uiColors = uiSettings;

// Feature flags (from settings.features)
const messagingEnabled = ref(false);
// Default OFF — only enabled when /v1/settings.features explicitly sets them true.
const directoryEnabled = ref(false);
const timeline3dEnabled = ref(false);

// Content data
const reactions = ref<AppReaction[]>([]);
const badges = ref<AppBadge[]>([]);
const genres = ref<AppGenre[]>([]);

// Notification types from /v1/settings/notification-types (name -> description template like "%s followed you")
const notificationTypes = ref<NotificationType[]>([]);
const notificationTypesLoaded = ref(false);

// Cache duration: 15 minutes
const CACHE_DURATION = 15 * 60 * 1000;

// Fallback used when /v1/settings does not provide maxVideoSize.
// Backend supports up to 10GB; mirror that here so the picker doesn't reject valid uploads.
const MAX_VIDEO_SIZE_FALLBACK = 10 * 1024 * 1024 * 1024;

/**
 * Check if site is music-only mode (can be used outside Vue setup context)
 * Music mode has been removed — always returns false.
 */
export function isMusicModeEnabled(): boolean {
  return false;
}

/**
 * Async version of isMusicModeEnabled.
 * Music mode has been removed — always returns false.
 */
export async function isMusicModeEnabledAsync(): Promise<boolean> {
  return false;
}

/**
 * Composable to load and access app settings from /v1/settings
 */
export function useAppSettings() {
  const { sdk } = useSdk();

  // Computed: reaction map for quick lookup
  const reactionMap = computed(() => {
    const map: Record<string, { emoji?: string; label?: string }> = {};
    reactions.value.forEach((r) => {
      if (r.name) {
        map[r.name] = {
          emoji: r.value,
          label: r.description || r.name,
        };
      }
    });
    return map;
  });

  // Computed: notification type map for quick lookup (name -> description template)
  const notificationTypeMap = computed(() => {
    const map: Record<string, string> = {};
    notificationTypes.value.forEach((t: NotificationType) => {
      if (t.name && t.description) {
        map[t.name] = t.description;
      }
    });
    return map;
  });

  // Computed: genre tree (top-level genres)
  const topLevelGenres = computed(() => {
    return genres.value.filter((g) => !g.parentId);
  });

  // Computed: is music mode (music features removed — always false)
  const isMusicMode = computed(() => false);

  // Computed: is social mode
  const isSocialMode = computed(() => {
    return siteType.value === "social" || siteType.value.includes("social");
  });

  // API SVG when present; otherwise gunclub-only vs tenant-neutral bundled mark.
  const brandLogoSvg = computed(() => resolveBrandLogoSvg(siteLogoSvg.value));

  /**
   * Whether the timeline should call getTimelinePlaylists and merge playlist cards.
   * Social-only tenants (siteType without "music") skip the request.
   * Optional override in API ui payload: timelinePlaylistsInFeed (boolean).
   */
  const maxVideoSize = computed<number>(() => {
    const ui = uiSettings.value as Record<string, unknown>;
    const raw = ui.maxVideoSize;
    if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
      return raw;
    }
    if (typeof raw === "string") {
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return MAX_VIDEO_SIZE_FALLBACK;
  });

  const timelinePlaylistFeedEnabled = computed(() => {
    const ui = uiSettings.value as Record<string, unknown>;
    if (ui.timelinePlaylistsInFeed === false) return false;
    if (ui.timelinePlaylistsInFeed === true) return true;
    const st = siteType.value.toLowerCase();
    if (!st) return true;
    return st.includes("music");
  });

  /**
   * Load settings from API (with caching)
   */
  async function loadSettings(force = false): Promise<void> {
    // Skip if already loaded and not forcing
    if (isLoaded.value && !force) {
      const now = Date.now();
      if (now - lastLoadedAt.value < CACHE_DURATION) {
        return;
      }
    }

    // Skip if already loading
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const response = await sdk.getGlobalSettings();
      const settings: AppSettings = response.data;

      // Extract site settings
      const ui = settings.siteSettings?.ui ?? {};

      // Site type (music, social, or music,social)
      // API returns camelCase: siteType (from ui-site-type tenant setting)
      if (ui.siteType) {
        console.log("[useAppSettings] Setting siteType:", ui.siteType);
        siteType.value = ui.siteType;
        localStorage.setItem("app_site_type", ui.siteType);
      } else {
        console.log("[useAppSettings] No siteType in API response, ui:", ui);
      }

      // Feed view mode configuration
      // API returns: feedViewMode (from ui-feed-view-mode tenant setting)
      // Values: 'single-only' | 'traditional-only' | 'both'
      // Also: feedViewModeDefault (from ui-feed-view-mode-default tenant setting)
      // Values: 'single-post' | 'traditional'
      const feedViewModeConfig = ui.feedViewMode as "single-only" | "traditional-only" | "both" | undefined;
      const feedViewModeDefault = ui.feedViewModeDefault as "single-post" | "traditional" | undefined;

      // Import UI store to update feed view mode config
      // Use dynamic import to avoid circular dependency
      import("@/stores/ui").then(({ useUiStore }) => {
        const uiStore = useUiStore();
        // The VITE_ENABLE_TRADITIONAL_FEED build flag intentionally WINS over
        // the API-driven feedViewModeConfig: when the legacy feed is disabled
        // (prod default), force single-only regardless of what the API returns.
        // An admin who sets `both`/`traditional-only` server-side has no effect
        // until the build flag is enabled.
        if (!TRADITIONAL_FEED_ENABLED) {
          uiStore.setFeedViewModeConfig("single-only", "single-post");
        } else if (feedViewModeConfig) {
          uiStore.setFeedViewModeConfig(
            feedViewModeConfig,
            feedViewModeDefault || "single-post",
          );
        } else {
          uiStore.setFeedViewModeConfig("both", "single-post");
        }
      });

      // Site logo - prefer 192px for header (optimal for 36px display at 3x retina)
      // Fallback chain: logo192 -> logo -> legacy siteLogo
      if (settings.siteSettings?.logos?.logo192) {
        siteLogo.value = settings.siteSettings.logos.logo192;
      } else if (settings.siteSettings?.logos?.logo) {
        siteLogo.value = settings.siteSettings.logos.logo;
      } else if (settings.siteSettings?.siteLogo) {
        siteLogo.value = settings.siteSettings.siteLogo;
      }

      // Inline brand SVG (not yet in the SDK SiteSettings type) — read
      // defensively from either siteLogoSvg or logos.logoSvg.
      const logoSvgRaw =
        (settings.siteSettings as { siteLogoSvg?: string } | undefined)?.siteLogoSvg ??
        (settings.siteSettings?.logos as { logoSvg?: string } | undefined)?.logoSvg ??
        null;
      siteLogoSvg.value = logoSvgRaw?.trim() || null;

      // Site favicon - update document favicon if tenant has custom one
      if (settings.siteSettings?.logos?.favicon) {
        siteFavicon.value = settings.siteSettings.logos.favicon;
        // Update the favicon link element in document head
        const faviconEl = document.getElementById("favicon") as HTMLLinkElement;
        if (faviconEl) {
          const faviconUrl = settings.siteSettings.logos.favicon;
          faviconEl.href = faviconUrl;
          // Set correct MIME type based on extension
          const isIco = faviconUrl.toLowerCase().endsWith(".ico");
          faviconEl.type = isIco ? "image/x-icon" : "image/png";
        }
      }

      // Website URL
      if (ui.website) {
        siteWebsite.value = ui.website;
      }

      // Loot nav label (ui-loot-menu-label tenant setting → ui.lootMenuLabel)
      const lootLabelRaw = (ui as { lootMenuLabel?: string }).lootMenuLabel;
      lootLabel.value = lootLabelRaw?.trim() || null;

      // Loot headline (ui-loot-headline tenant setting → ui.lootHeadline)
      const lootHeadlineRaw = (ui as { lootHeadline?: string }).lootHeadline;
      lootTagline.value = lootHeadlineRaw?.trim() || null;

      // UI mode
      if (ui.mode && ["light", "dark", "both"].includes(ui.mode)) {
        uiMode.value = ui.mode as "light" | "dark" | "both";
      }

      // Store all UI settings (includes all colors and config)
      uiSettings.value = { ...ui };
      console.log("[useAppSettings] Stored uiSettings:", {
        keys: Object.keys(ui),
        primaryColorDark: ui.primaryColorDark,
        mode: ui.mode,
      });

      // Reactions
      if (settings.reactions && settings.reactions.length > 0) {
        reactions.value = settings.reactions;
      }

      // Badges
      if (settings.badges && settings.badges.length > 0) {
        badges.value = settings.badges;
      }

      // Genres
      if (settings.genres && settings.genres.length > 0) {
        genres.value = settings.genres;
      }

      // Feature flags
      // SDK type only declares `messaging`; directory/timeline3d are read
      // defensively so unknown/absent keys default OFF.
      const features = (settings.features ?? {}) as Record<string, unknown>;
      messagingEnabled.value = features.messaging === true;
      directoryEnabled.value = features.directory === true;
      timeline3dEnabled.value = features.timeline3d === true;

      // User notification types from /v1/settings may be present (legacy)
      // but we prefer the dedicated endpoint loaded separately
      if (
        settings.userNotificationTypes &&
        settings.userNotificationTypes.length > 0 &&
        !notificationTypesLoaded.value
      ) {
        notificationTypes.value = settings.userNotificationTypes;
      }

      isLoaded.value = true;
      lastLoadedAt.value = Date.now();

      // Also load notification types from the dedicated endpoint
      loadNotificationTypes();
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to load settings";
      console.error("Failed to load app settings:", err);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Load notification types from dedicated /v1/settings/notification-types endpoint
   */
  async function loadNotificationTypes(force = false): Promise<void> {
    if (notificationTypesLoaded.value && !force) {
      return;
    }

    try {
      // TODO: Backend route /v1/settings/notification-types not yet implemented
      // SDK method exists, but backend endpoint is missing from OpenAPI spec
      // Uncomment when backend route is added
      // const response = await sdk.getNotificationTypes();
      // if (
      //   response.data &&
      //   Array.isArray(response.data) &&
      //   response.data.length > 0
      // ) {
      //   notificationTypes.value = response.data;
      //   console.log(
      //     "[useAppSettings] Loaded notification types from /v1/settings/notification-types:",
      //     response.data.length,
      //   );
      // }
      notificationTypesLoaded.value = true;
    } catch (err) {
      // Non-critical - we may already have types from /v1/settings
      console.warn(
        "Failed to load notification types from dedicated endpoint:",
        err,
      );
    }
  }

  /**
   * Invalidate cache and force reload on next access
   */
  function invalidateCache(): void {
    lastLoadedAt.value = 0;
    isLoaded.value = false;
    notificationTypesLoaded.value = false;
  }

  /**
   * Get reaction emoji by name
   */
  function getReactionEmoji(name: string): string | undefined {
    return reactionMap.value[name]?.emoji;
  }

  /**
   * Get genre by ID
   */
  function getGenreById(id: number): AppGenre | undefined {
    return genres.value.find((g) => g.id === id);
  }

  /**
   * Get genre by slug
   */
  function getGenreBySlug(slug: string): AppGenre | undefined {
    return genres.value.find((g) => g.slug === slug);
  }

  /**
   * Get child genres for a parent
   */
  function getChildGenres(parentId: number): AppGenre[] {
    return genres.value.filter((g) => g.parentId === parentId);
  }

  /**
   * Get notification description template by type name
   * Returns the template string like "%s followed you" or undefined if not found
   */
  function getNotificationDescription(typeName: string): string | undefined {
    return notificationTypeMap.value[typeName];
  }

  /**
   * Format notification text using the template and actor name
   * Example: getFormattedNotificationText('follow', 'John') => 'John followed you'
   */
  function getFormattedNotificationText(
    typeName: string,
    actorName: string,
  ): string | undefined {
    const template = notificationTypeMap.value[typeName];
    if (!template) return undefined;
    // Replace %s placeholder with actor name
    return template.replace("%s", actorName);
  }

  return {
    // State (readonly)
    isLoaded: readonly(isLoaded),
    isLoading: readonly(isLoading),
    error: readonly(error),

    // Site settings (readonly)
    siteType: readonly(siteType),
    siteLogo: readonly(siteLogo),
    siteLogoSvg: readonly(siteLogoSvg),
    siteFavicon: readonly(siteFavicon),
    siteWebsite: readonly(siteWebsite),
    lootLabel: readonly(lootLabel),
    lootTagline: readonly(lootTagline),
    uiMode: readonly(uiMode),
    uiSettings: readonly(uiSettings),
    uiColors: readonly(uiColors), // Legacy alias

    // Content data (readonly)
    reactions: readonly(reactions),
    badges: readonly(badges),
    genres: readonly(genres),
    notificationTypes: readonly(notificationTypes),

    // Feature flags (readonly)
    messagingEnabled: readonly(messagingEnabled),
    directoryEnabled: readonly(directoryEnabled),
    timeline3dEnabled: readonly(timeline3dEnabled),

    // Computed
    brandLogoSvg,
    reactionMap,
    notificationTypeMap,
    topLevelGenres,
    isMusicMode,
    isSocialMode,
    timelinePlaylistFeedEnabled,
    maxVideoSize,

    // Actions
    loadSettings,
    loadNotificationTypes,
    invalidateCache,

    // Helpers
    getReactionEmoji,
    getGenreById,
    getGenreBySlug,
    getChildGenres,
    getNotificationDescription,
    getFormattedNotificationText,
  };
}

// Export type
export type UseAppSettingsReturn = ReturnType<typeof useAppSettings>;
